import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { MileageTripData, VehicleData } from '@/types/api';

const KEY = ['mileage'] as const;

export type VehicleInput = {
  name: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  date_placed_in_service?: string | null;
  available_for_personal_use?: boolean;
  has_another_vehicle?: boolean;
  is_active?: boolean;
  notes?: string | null;
};

export type TripInput = {
  vehicle_id: number;
  trip_date: string;
  purpose: string;
  business_miles: number;
  odometer_start?: number | null;
  odometer_end?: number | null;
  client_id?: number | null;
  notes?: string | null;
};

export type VehicleMileage = {
  vehicle_id: number;
  name: string;
  business_miles: number;
  date_placed_in_service: string | null;
  available_for_personal_use: boolean;
  has_another_vehicle: boolean;
};

export type MileageSummary = {
  year: number;
  total_business_miles: number;
  rate_cents_per_mile: number | null;
  has_rate: boolean;
  deduction_cents: number | null;
  by_vehicle: VehicleMileage[];
  available_years: number[];
};

// --- Vehicles ---------------------------------------------------------------

export function useVehicles() {
  return useQuery({
    queryKey: [...KEY, 'vehicles'],
    queryFn: async (): Promise<VehicleData[]> => {
      const { data } = await api.get<VehicleData[]>('/v1/accounting/vehicles');
      return data;
    },
  });
}

export function useSaveVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id?: number; input: VehicleInput }): Promise<VehicleData> => {
      await primeCsrf();
      const { data } = id
        ? await api.patch<VehicleData>(`/v1/accounting/vehicles/${id}`, input)
        : await api.post<VehicleData>('/v1/accounting/vehicles', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/accounting/vehicles/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// --- Trips ------------------------------------------------------------------

export function useMileageTrips(filters: { year?: number; vehicle_id?: number } = {}) {
  return useQuery({
    queryKey: [...KEY, 'trips', filters],
    queryFn: async (): Promise<MileageTripData[]> => {
      const { data } = await api.get<MileageTripData[]>('/v1/accounting/mileage/trips', {
        params: filters,
      });
      return data;
    },
  });
}

export function useSaveTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id?: number; input: TripInput }): Promise<MileageTripData> => {
      await primeCsrf();
      const { data } = id
        ? await api.patch<MileageTripData>(`/v1/accounting/mileage/trips/${id}`, input)
        : await api.post<MileageTripData>('/v1/accounting/mileage/trips', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/accounting/mileage/trips/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// --- Summary + rates --------------------------------------------------------

export function useMileageSummary(year: number) {
  return useQuery({
    queryKey: [...KEY, 'summary', year],
    queryFn: async (): Promise<MileageSummary> => {
      const { data } = await api.get<MileageSummary>('/v1/accounting/mileage/summary', {
        params: { year },
      });
      return data;
    },
  });
}

export function useSetMileageRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { year: number; cents_per_mile: number }): Promise<void> => {
      await primeCsrf();
      await api.put('/v1/accounting/mileage/rates', input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
